import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import './CreatePlanModal.css';

const CreatePlanModal = ({ isOpen, onClose, profile, onPlanCreated }) => {
  const { authFetch } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    goalRace: '5k',
    planLength: '16',
    hasSpecificDate: true,
    goalRaceDate: '',
    startDate: '',
    intensity: 'high_performance',
    longRunDay: 'sunday'
  });

  // Pre-fill from profile when modal opens
  useEffect(() => {
    if (isOpen && profile) {
      const today = new Date();
      const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);
      const defaultRaceDate = new Date(today.getTime() + 16 * 7 * 24 * 60 * 60 * 1000); // 16 weeks from now

      setFormData({
        goalRace: profile.goal_race || profile.goalRace || '5k',
        planLength: '16',
        hasSpecificDate: true,
        goalRaceDate: profile.goal_race_date || profile.goalRaceDate || defaultRaceDate.toISOString().split('T')[0],
        startDate: tomorrow.toISOString().split('T')[0],
        intensity: profile.training_level || 'high_performance',
        longRunDay: profile.long_run_day || profile.longRunDay || 'sunday'
      });
      setError(null);
    }
  }, [isOpen, profile]);

  const raceOptions = [
    { value: '1500m', label: '1500m' },
    { value: '5k', label: '5K' },
    { value: '10k', label: '10K' },
    { value: 'half_marathon', label: 'Half Marathon' },
    { value: 'marathon', label: 'Marathon' },
    { value: 'ultra_marathon', label: 'Ultra Marathon' },
  ];

  const planLengthOptions = [
    { value: '16', label: '16 weeks' },
    { value: '20', label: '20 weeks' },
    { value: '24', label: '24 weeks' },
  ];

  // Mileage ranges by race and intensity level (km per week)
  const mileageRanges = {
    '1500m': { performance: '48-64', high_performance: '64-80', elite: '80-96' },
    '5k': { performance: '64-80', high_performance: '80-96', elite: '96-112' },
    '10k': { performance: '64-80', high_performance: '80-96', elite: '96-112' },
    'half_marathon': { performance: '80-104', high_performance: '104-128', elite: '128-152' },
    'marathon': { performance: '96-120', high_performance: '120-144', elite: '144-168' },
    'ultra_marathon': { performance: '112-136', high_performance: '136-160', elite: '160-192' },
  };

  const getIntensityOptions = (goalRace) => {
    const ranges = mileageRanges[goalRace] || mileageRanges['5k'];
    return [
      { value: 'performance', label: 'Performance', description: `${ranges.performance} km/week` },
      { value: 'high_performance', label: 'High Performance', description: `${ranges.high_performance} km/week` },
      { value: 'elite', label: 'Elite', description: `${ranges.elite} km/week` },
    ];
  };

  const dayOptions = [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' },
    { value: 'sunday', label: 'Sunday' },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      let raceDate;
      const planWeeks = parseInt(formData.planLength);

      if (formData.hasSpecificDate) {
        // Validate race date is far enough in the future for the plan
        raceDate = new Date(formData.goalRaceDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const minRaceDate = new Date(today.getTime() + planWeeks * 7 * 24 * 60 * 60 * 1000);

        if (raceDate < minRaceDate) {
          throw new Error(`Race date must be at least ${planWeeks} weeks from today for a ${planWeeks}-week plan`);
        }
      } else {
        // Calculate race date based on start date + plan length
        const startDate = new Date(formData.startDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (startDate < today) {
          throw new Error('Start date must be today or in the future');
        }

        raceDate = new Date(startDate.getTime() + planWeeks * 7 * 24 * 60 * 60 * 1000);
      }

      const raceDateStr = raceDate.toISOString().split('T')[0];

      // Map intensity to experience level for API
      const experienceLevelMap = {
        'performance': 'beginner',
        'high_performance': 'intermediate',
        'elite': 'advanced'
      };

      // Get VDOT and weekly km from profile
      const vdot = profile?.fitness_level || profile?.vdot || 45;
      const currentWeeklyKm = profile?.current_weekly_km || 40;

      // Generate plan
      const generateResponse = await authFetch('/api/training-plans/generate', {
        method: 'POST',
        body: JSON.stringify({
          vdot: parseFloat(vdot),
          goal_race: formData.goalRace,
          goal_race_date: raceDateStr,
          current_weekly_km: parseFloat(currentWeeklyKm),
          experience_level: experienceLevelMap[formData.intensity] || 'intermediate',
          long_run_day: formData.longRunDay
        })
      });

      if (!generateResponse.ok) {
        const errorData = await generateResponse.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to generate plan');
      }

      const generateData = await generateResponse.json();

      // Save plan
      const saveResponse = await authFetch('/api/user/training-plan', {
        method: 'POST',
        body: JSON.stringify({ plan_data: generateData.plan })
      });

      if (!saveResponse.ok) {
        throw new Error('Failed to save plan');
      }

      // Update profile with new goals
      await authFetch('/api/user/profile', {
        method: 'PUT',
        body: JSON.stringify({
          goal_race: formData.goalRace,
          goal_race_date: raceDateStr,
          training_level: formData.intensity,
          long_run_day: formData.longRunDay
        })
      });

      onPlanCreated(generateData.plan);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // Calculate minimum race date based on plan length
  const planWeeks = parseInt(formData.planLength);
  const minRaceDate = new Date();
  minRaceDate.setDate(minRaceDate.getDate() + (planWeeks * 7));
  const minRaceDateStr = minRaceDate.toISOString().split('T')[0];

  // Calculate minimum start date (today)
  const today = new Date();
  const minStartDateStr = today.toISOString().split('T')[0];

  return (
    <div className="create-plan-modal-overlay" onClick={onClose}>
      <div className="create-plan-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>&times;</button>

        <div className="modal-header">
          <h2>Create New Training Plan</h2>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          {error && <div className="modal-error">{error}</div>}

          {/* Goal Race Distance */}
          <div className="form-group">
            <label htmlFor="goalRace">Goal Race Distance</label>
            <select
              id="goalRace"
              name="goalRace"
              value={formData.goalRace}
              onChange={handleChange}
            >
              {raceOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Training Plan Length */}
          <div className="form-group">
            <label htmlFor="planLength">Training Plan Length</label>
            <select
              id="planLength"
              name="planLength"
              value={formData.planLength}
              onChange={handleChange}
            >
              {planLengthOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Specific Race Date Checkbox */}
          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="hasSpecificDate"
                checked={formData.hasSpecificDate}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  hasSpecificDate: e.target.checked
                }))}
              />
              <span>I have a specific race date</span>
            </label>
          </div>

          {/* Race Date - only show if checkbox is checked */}
          {formData.hasSpecificDate ? (
            <div className="form-group">
              <label htmlFor="goalRaceDate">Race Date</label>
              <input
                type="date"
                id="goalRaceDate"
                name="goalRaceDate"
                value={formData.goalRaceDate}
                onChange={handleChange}
                min={minRaceDateStr}
                required
              />
              <span className="form-hint">Must be at least {planWeeks} weeks from today</span>
            </div>
          ) : (
            <div className="form-group">
              <label htmlFor="startDate">Plan Start Date</label>
              <input
                type="date"
                id="startDate"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                min={minStartDateStr}
                required
              />
              <span className="form-hint">Your plan will end {planWeeks} weeks from this date</span>
            </div>
          )}

          {/* Training Plan Intensity */}
          <div className="form-group">
            <label htmlFor="intensity">Training Plan Intensity</label>
            <select
              id="intensity"
              name="intensity"
              value={formData.intensity}
              onChange={handleChange}
            >
              {getIntensityOptions(formData.goalRace).map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label} ({opt.description})
                </option>
              ))}
            </select>
          </div>

          {/* Preferred Long Run Day */}
          <div className="form-group">
            <label htmlFor="longRunDay">Preferred Long Run Day</label>
            <select
              id="longRunDay"
              name="longRunDay"
              value={formData.longRunDay}
              onChange={handleChange}
            >
              {dayOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-save"
              disabled={loading}
            >
              {loading ? 'Generating...' : 'Create Plan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreatePlanModal;
